"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

const PreviousPageContext = createContext(null);

/**
 * Tracks the previous full path (pathname + query string) the user
 * navigated from, within this SPA session. `null` until at least one
 * client-side navigation has happened (e.g. a fresh page load/refresh has
 * no known "previous" page).
 *
 * Deliberately keys off `usePathname()` alone rather than `useSearchParams()`:
 * this provider lives in the persistent dashboard layout, and `useSearchParams`
 * there opts every route beneath it out of static prerendering (and fails the
 * build unless it's wrapped in Suspense). Watching only the pathname also stops
 * the query-string rewrites `useUrlListState` issues on every keystroke from
 * being recorded as page changes.
 *
 * Consequence: the stored query string is the one the page was *entered* with,
 * not the one it was left with. `useContextualBack` only uses this value for
 * its label (where `getPageLabel` strips the query) and navigates with
 * `router.back()`, which restores the real history entry — so don't use this
 * value as a `router.push` target.
 */
export function PreviousPageProvider({ children }) {
  const pathname = usePathname();

  const currentRef = useRef(null);
  const [previousPath, setPreviousPath] = useState(null);

  useEffect(() => {
    const fullPath = window.location.pathname + window.location.search;
    const departedFrom = currentRef.current;
    currentRef.current = fullPath;

    // The first run is the initial page load — there's nothing to go back to.
    if (departedFrom !== null && departedFrom !== fullPath) {
      setPreviousPath(departedFrom);
    }
  }, [pathname]);

  return (
    <PreviousPageContext.Provider value={previousPath}>
      {children}
    </PreviousPageContext.Provider>
  );
}

export function usePreviousPage() {
  return useContext(PreviousPageContext);
}
